import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { get, set, has, unset, toNumber } from 'lodash';
import { exchangeTypes } from 'new/components/settings/VendorBuilder/selectTypes';
import { EndpointType } from 'new/components/settings/VendorBuilder/types';
import validateVendor from 'new/components/settings/VendorBuilder/validateVendor';
import LocalStorageUtils from 'js/lib/LocalStorageUtils';
import parseVendorToVendorSpec from 'new/components/settings/VendorBuilder/parseVendorToVendorSpec';

const baseTemplate = {
  name: '',
  display_name: '',
  config: {
    'test.url': '',
    'auth.type': '',
    'test.method': '',
    'request.headers': '',
    'test.content.type': '',
  },
  params: [],
};

const baseEndpoint = (vendor) => ({
  name: '',
  vendor_name: '',
  display_name: '',
  description: '',
  parameters: [],
  config: {
    info_url: '',
  },
});

export const baseParameter = (vendor) => ({
  vendor_endpoint_name: '',
  name: '',
  display_name: '',
  description: '',
  order: vendor?.parameters?.length || 0,
  resource_type: '',
  data_type: '',
  config: {
    required: false,
    default: '',
    can_add_values: false,
  },
  allowed_values: undefined,
});

const initialBaseEndpoint = baseEndpoint({ name: '' });
const initialBaseParameter = baseParameter({});

const initialEndpoints = [] as EndpointType[];

const auth_templates = [] as (typeof baseTemplate)[];

export const initialVendorState = {
  name: '',
  display_name: '',
  description: '',
  config: {
    connectionCategory: 'templatized_api',
  },
  connection_type: 'rest',
  auth_templates,
  dynamicValues: {},
  endpoints: initialEndpoints,
};

export type VendorType = typeof initialVendorState & { [key: string]: any };

type ContextValue = {
  vendor: VendorType;
  errors: { [key: string]: string };
  onChange: (e: any) => void;
  onAddTemplate: (e: any) => void;
  onClear: () => void;
  onValidateVendor: () => void;
  addNameChangeListener: (e: any) => void;
  removeNameChangeListener: (e: any) => void;
  updateVendor: (updater: (newVendor: VendorType) => void) => void;
  onAddEndpoint: () => void;
};

const VendorBuilderCtx = createContext<ContextValue>({
  vendor: initialVendorState,
  errors: {},
  onChange: (e) => {},
  onAddTemplate: (e) => {},
  onClear: () => {},
  onValidateVendor: () => {},
  addNameChangeListener: (e) => {},
  removeNameChangeListener: (e) => {},
  updateVendor: (updater) => {},
  onAddEndpoint: () => {},
});

export function useVendorBuilder() {
  return useContext(VendorBuilderCtx);
}

export const VendorBuilderContext = (props: PropsWithChildren<any>) => {
  const savedVendorStr = LocalStorageUtils.getObject('vendor-builder-obj');
  const [vendor, setVendor] = useState<any>(savedVendorStr || initialVendorState);
  const saveRef = useRef(null);
  const [errors, setErrors] = useState<any>({});
  const nameChangeListeners = useRef([]);

  useEffect(() => {
    saveRef.current = setInterval(() => {
      setVendor((newVendor) => {
        LocalStorageUtils.saveObject('vendor-builder-obj', newVendor);
        return newVendor;
      });
    }, 3000);

    return () => {
      // prevent clearing the saved vendor object when leaving the page
      clearTimeout(saveRef.current);
    };
  }, []);

  const updateVendor = useCallback((updater: (newVendor: VendorType) => void) => {
    setVendor((prevVendor: VendorType) => {
      const newVendor = { ...prevVendor };
      try {
        updater(newVendor);
      } catch (e) {}
      return newVendor;
    });
  }, []);

  const addNameChangeListener = (listener: (name: string) => void) => {
    if (typeof listener === 'function' && !nameChangeListeners.current.includes(listener)) {
      nameChangeListeners.current.push(listener);
    }
  };

  const removeNameChangeListener = (listener: (name: string) => void) => {
    const index = nameChangeListeners.current.findIndex(listener);
    if (index >= 0) {
      nameChangeListeners.current.splice(index, 1);
    }
  };

  const onAddTemplate = () => {
    updateVendor((newVendor) => {
      newVendor.auth_templates.push(JSON.parse(JSON.stringify(baseTemplate)));
    });
  };

  const onAddEndpoint = () => {
    updateVendor((newVendor) => {
      newVendor.endpoints.push(baseEndpoint(vendor));
    });
  };

  useEffect(() => {
    nameChangeListeners.current.forEach((listener) => {
      try {
        listener(vendor.name);
      } catch (e) {
        console.log('Error while trying to listen to name update', e);
      }
    });
  }, [vendor.name]);

  const onChangeVendor = useCallback(
    (e) => {
      const { value: v, name, checked, type } = e.target;
      let value;
      if (type === 'checkbox') {
        value = checked;
      } else {
        value = type === 'number' ? toNumber(v) : v;
      }
      updateVendor((newVendor) => {
        if (name.includes('auth_templates')) {
          if (name.includes('auth.type') && value.includes('OAUTH')) {
            //adding token exchange type in vendor
            const exchangeName = name.replace(
              'auth.type',
              `${value.toLowerCase()}.token_exchange_type`,
            );
            const exchangeNameToDelete =
              value === 'OAUTH1'
                ? name.replace('auth.type', `oauth2.token_exchange_type`)
                : name.replace('auth.type', `oauth1.token_exchange_type`);
            if (!get(newVendor, exchangeName)) {
              set(newVendor, exchangeName, exchangeTypes[0].value);
            }
            if (has(newVendor, exchangeNameToDelete)) {
              unset(newVendor, exchangeNameToDelete);
            }
          } else if (name.includes('auth.type') && !value.includes('OAUTH')) {
            //check if vendor already has a token exchange name set. in that case remove it.
            let exchangeNamesToDelete = [
              name.replace('auth.type', `oauth1.token_exchange_type`),
              name.replace('auth.type', `oauth2.token_exchange_type`),
            ];
            exchangeNamesToDelete.forEach((t) => {
              if (has(newVendor, t)) {
                unset(newVendor, t);
              }
            });
          }
        }
        set(newVendor, name, value);
      });
    },
    [updateVendor],
  );

  const onValidateVendor = () => {
    setErrors(validateVendor(JSON.parse(parseVendorToVendorSpec(vendor))));
  };

  const onClear = () => {
    LocalStorageUtils.removeObject('vendor-builder-obj');
    setErrors({});
    setVendor(initialVendorState);
  };

  const value = useMemo(() => {
    return {
      vendor,
      errors,
      onChange: onChangeVendor,
      onAddTemplate,
      onClear,
      onValidateVendor,
      removeNameChangeListener,
      addNameChangeListener,
      onAddEndpoint,
      updateVendor,
    };
  }, [vendor, onChangeVendor, errors]);

  return <VendorBuilderCtx.Provider value={value}>{props.children}</VendorBuilderCtx.Provider>;
};
